/**
 * 状态彩色标签组件。
 *
 * 统一封装 MUI Chip，便于各页面以一致方式展示状态。
 * 兼容两种用法：
 *  1) 传入 status + labelMap/colorMap，由组件完成状态映射；
 *  2) 直接传入 label + color，由调用方自行决定文案与颜色。
 *
 * @module components/common/StatusChip
 */
import Chip from '@mui/material/Chip';
import type { ChipProps } from '@mui/material/Chip';
import type { ReactNode } from 'react';

/** 可用的标签颜色。 */
type ChipColor = ChipProps['color'];

/** StatusChip 组件属性。 */
interface StatusChipProps {
  /** 状态值（配合 labelMap / colorMap 使用）。 */
  status?: string;
  /** 状态 → 文案映射。 */
  labelMap?: Record<string, string>;
  /** 状态 → 颜色映射。 */
  colorMap?: Record<string, ChipColor>;
  /** 直接文案（未传 status 时使用）。 */
  label?: ReactNode;
  /** 直接颜色（未传 status 时使用，默认 default）。 */
  color?: ChipColor;
  /** 变体（默认 filled）。 */
  variant?: ChipProps['variant'];
  /** 尺寸（默认 small）。 */
  size?: ChipProps['size'];
}

/**
 * 状态彩色标签组件。
 *
 * @param props 组件属性。
 * @returns 标签元素。
 */
export default function StatusChip({
  status,
  labelMap,
  colorMap,
  label,
  color = 'default',
  variant = 'filled',
  size = 'small',
}: StatusChipProps): JSX.Element {
  const text: ReactNode =
    status !== undefined ? (labelMap?.[status] ?? status) : label;
  const chipColor: ChipColor =
    status !== undefined ? (colorMap?.[status] ?? 'default') : color;

  return (
    <Chip label={text} color={chipColor} variant={variant} size={size} />
  );
}
